import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { forkJoin, of, Subscription } from 'rxjs';
import { EstadoRutaDistancia, TramosDistanciaService } from '../../services/tramos-distancia.service';
import { formatoNumero, formatoPct } from './franjas-editor.rules';
import * as D from './tramos-distancia.rules';
import {
  TramosDistanciaEditorComponent,
  TramosDistanciaEditorData,
} from './tramos-distancia-editor.component';

/**
 * Bloque de Oscar «Lo que cotiza el motor ahora» para una ruta USDT
 * (23/10/2026). Reemplaza, en el panel, el rango de tramos, el gráfico y la
 * tabla A/B/C contra Binance que los operadores tacharon:
 *
 * - PISO y TECHO (nombres de Oscar según la ruta) = la chica y la grande.
 * - Margen contra el promedio P2P (o el spot en CLP) y el colchón.
 * - Rango que reparte la calculadora (del peor al mejor, por monto).
 * - Fuente (Tasa Negociación / spot) y la comisión, de solo lectura.
 * - La tabla de distancia con la fila activa marcada y su tasa resultante.
 *
 * Solo pinta lo que manda el API (`GET rates/tramos-usdt/{ruta}` y
 * `GET rates/tramos-distancia/:familia`). Si el API no usa el modelo de
 * distancia (API vieja o `MARGEN_MIN`) no muestra nada y emite
 * `modelo = false`, para que el panel siga como siempre.
 */
@Component({
  selector: 'app-distancia-bloque',
  templateUrl: './distancia-bloque.component.html',
  styleUrls: ['./distancia-bloque.component.sass'],
})
export class DistanciaBloqueComponent implements OnChanges, OnDestroy {
  @Input() ruta: D.RutaUsdt = 'USDT-VES';
  /**
   * Estado de la ruta si el contenedor ya lo leyó (VES: el bloque «Dónde
   * está cada precio»). `undefined` = lo lee este bloque al abrirse.
   */
  @Input() estado?: EstadoRutaDistancia | null;
  /** Comisión configurada (VES), para la cuenta del PISO/TECHO. */
  @Input() comisionPct: number | null = null;
  /** La sección está abierta: plegada no consulta nada. */
  @Input() activo = true;
  /** Respaldo del permiso si el API no manda `puedeEditar`. */
  @Input() puedeEditar = false;
  /**
   * Contador del panel: cada vez que cambia (el panel releyó la sección) el
   * bloque vuelve a leer su estado y su tabla. Sin él, solo al abrirse.
   */
  @Input() recarga = 0;

  /** true = el API usa el modelo de distancia y el bloque se muestra. */
  @Output() modelo = new EventEmitter<boolean>();
  /** La tabla se guardó: el contenedor puede releer sus tasas. */
  @Output() guardado = new EventEmitter<D.FamiliaDistancia>();

  estadoPropio: EstadoRutaDistancia | null = null;
  tabla: D.TablaDistanciaResponse | null = null;
  filas: D.FilaDistancia[] = [];
  vista: D.VistaDistancia | null = null;
  activa = -1;
  cargando = false;

  private sub?: Subscription;
  private ultimoModelo: boolean | null = null;

  constructor(
    private svc: TramosDistanciaService,
    private dialog: MatDialog,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    const recargo = !!changes['recarga'] && !changes['recarga'].firstChange;
    if (changes['activo'] || changes['ruta'] || recargo || (changes['estado'] && this.estado !== undefined)) {
      if (this.activo) this.cargar();
    }
    this.recalcular();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  get familia(): D.FamiliaDistancia {
    return D.familiaDe(this.ruta);
  }

  get rotulos(): D.RotulosDistancia {
    return D.rotulosDistancia(this.ruta);
  }

  get esVes(): boolean {
    return this.familia === 'VES';
  }

  /** Permiso: manda el API (`puedeEditar` de la tabla); si no vino, el del panel. */
  get editable(): boolean {
    return typeof this.tabla?.puedeEditar === 'boolean' ? this.tabla.puedeEditar : this.puedeEditar;
  }

  private get estadoVigente(): EstadoRutaDistancia | null {
    return this.estado !== undefined ? this.estado ?? null : this.estadoPropio;
  }

  /** Lee el estado (si no lo da el contenedor) y la tabla. Nunca lanza. */
  cargar(): void {
    if (!this.activo) return;
    this.sub?.unsubscribe();
    const propio = this.estado === undefined;
    // Con el estado del contenedor sin modelo de distancia no hace falta la tabla.
    if (!propio && !D.esModeloDistancia(this.estado)) {
      this.tabla = null;
      this.recalcular();
      return;
    }
    this.cargando = true;
    this.sub = forkJoin({
      estado: propio ? this.svc.obtenerEstadoRuta(this.ruta) : of(this.estado ?? null),
      tabla: this.svc.leerTabla(this.familia),
    }).subscribe(({ estado, tabla }) => {
      this.cargando = false;
      if (propio) this.estadoPropio = estado;
      this.tabla = tabla;
      this.recalcular();
    });
  }

  recalcular(): void {
    const estado = this.estadoVigente;
    this.vista = D.vistaDistancia(this.ruta, estado, this.comisionPct);
    this.filas = D.normalizarFilas(this.tabla?.filas);
    const porRuta = D.normalizarActiva(this.tabla).find((a) => a.ruta === this.ruta);
    this.activa = this.vista
      ? D.indiceFilaActiva(this.filas, this.vista.margenPct, estado?.filaDistancia ?? porRuta?.fila ?? null)
      : -1;
    const hay = !!this.vista;
    if (hay !== this.ultimoModelo) {
      this.ultimoModelo = hay;
      // Fuera del ciclo de detección: el contenedor oculta lo viejo con esto.
      Promise.resolve().then(() => this.modelo.emit(hay));
    }
  }

  /** Tasa resultante de la fila: la oficial (`grande`) en la activa; las demás, estimadas. */
  resultante(i: number): number | null {
    if (!this.vista) return null;
    if (i === this.activa && this.vista.mejor != null) return this.vista.mejor;
    return D.tasaResultante(this.ruta, this.vista.peor, this.filas[i]?.ajustePct);
  }

  cuentaPeor(): string {
    return this.vista ? D.cuentaPeor(this.vista) : '';
  }

  n(x: number | null | undefined, dec = 2): string {
    return formatoNumero(x, dec);
  }

  /**
   * Porcentaje con 4 decimales como la calculadora de Oscar: el margen con
   * signo («+0,9200 %») y el rango sin él («0,6700 %»).
   */
  pct4(x: number | null | undefined, signo = true): string {
    return formatoPct(x, { signo, dec: 4 });
  }

  /** CLP→USDT: la tabla rige desde la próxima tasa base (lo dice el API; si no, por la ruta). */
  get rigeDesdeProximaBase(): boolean {
    const r = this.tabla?.rigeDesdeProximaBase;
    return typeof r === 'boolean' ? r : this.ruta === 'CLP-USDT' && !!this.tabla;
  }

  pct(x: number | null | undefined): string {
    return formatoPct(x);
  }

  /** «0,3» / «∞». */
  limite(x: number | null | undefined): string {
    return x == null ? '∞' : Number(x).toLocaleString('es-CL', { maximumFractionDigits: 4 });
  }

  /** Abre el editor de la tabla de la familia (sin permiso, en solo lectura). */
  abrirEditor(): void {
    const movil = window.innerWidth < 768;
    const ref = this.dialog.open<TramosDistanciaEditorComponent, TramosDistanciaEditorData>(
      TramosDistanciaEditorComponent,
      {
        data: { familia: this.familia, puedeEditar: this.editable, rutaInicial: this.ruta },
        width: movil ? '100vw' : '1100px',
        maxWidth: movil ? '100vw' : '96vw',
        height: movil ? '100vh' : '90vh',
        maxHeight: movil ? '100vh' : '90vh',
        autoFocus: 'first-tabbable',
        restoreFocus: true,
        disableClose: true,
        panelClass: 'franjas-dialog',
        ariaLabelledBy: 'td-titulo',
      },
    );
    ref.afterClosed().subscribe((guardo) => {
      if (!guardo) return;
      this.svc.olvidar();
      this.guardado.emit(this.familia);
      this.cargar();
    });
  }

  trackIndice(i: number): number {
    return i;
  }
}
